/**
 * Chat Message Component
 *
 * Renders a single message in the chat history with support for:
 * - User and assistant messages
 * - Tool calls and results
 * - Entity cards (projects, agents, etc.)
 * - Streaming indicator
 */

import { A } from '@solidjs/router';
import { type Component, For, Show } from 'solid-js';
import type { EntityReference, Message, ToolCall, ToolResult } from './types';

interface ChatMessageProps {
  message: Message;
}

// Tool call display
const ToolCallCard: Component<{ toolCall: ToolCall }> = (props) => (
  <div class="bg-gray-700/50 rounded-lg p-3 mt-2 text-sm">
    <div class="flex items-center gap-2 text-purple-400 font-medium mb-1">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      {formatToolName(props.toolCall.name)}
    </div>
    <pre class="text-gray-400 text-xs overflow-x-auto">
      {JSON.stringify(props.toolCall.arguments, null, 2)}
    </pre>
  </div>
);

// Tool result display
const ToolResultCard: Component<{ result: ToolResult }> = (props) => (
  <div
    class={`rounded-lg p-3 mt-2 text-sm ${
      props.result.success ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'
    }`}
  >
    <div
      class={`flex items-center gap-2 font-medium mb-1 ${props.result.success ? 'text-green-400' : 'text-red-400'}`}
    >
      {props.result.success ? (
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : (
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      )}
      {props.result.success ? 'Success' : 'Error'}
    </div>
    <Show when={props.result.error}>
      <p class="text-red-300 text-xs">{props.result.error}</p>
    </Show>
    <Show when={props.result.result}>
      <pre class="text-gray-300 text-xs overflow-x-auto mt-1">
        {JSON.stringify(props.result.result, null, 2)}
      </pre>
    </Show>
  </div>
);

// Entity reference card
const EntityCard: Component<{ entity: EntityReference }> = (props) => {
  const getIcon = () => {
    switch (props.entity.type) {
      case 'project':
        return (
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
        );
      case 'agent':
        return (
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case 'workflow':
        return (
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
            />
          </svg>
        );
      case 'skill':
        return (
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        );
      case 'expression':
        return (
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getHref = () => {
    switch (props.entity.type) {
      case 'project':
        return `/projects/${props.entity.id}`;
      case 'agent':
        return `/projects/${props.entity.id}`;
      case 'workflow':
        return `/projects/${props.entity.id}`;
      case 'skill':
        return `/skills/${props.entity.id}`;
      case 'expression':
        return `/artifacts/${props.entity.id}`;
      default:
        return '#';
    }
  };

  return (
    <A
      href={getHref()}
      class="flex items-center gap-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg p-3 mt-2 transition-colors no-underline"
    >
      <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
        {getIcon()}
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-white font-medium truncate">{props.entity.name}</div>
        <div class="text-gray-400 text-sm capitalize">{props.entity.type}</div>
      </div>
      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </A>
  );
};

// Format tool name for display
function formatToolName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Streaming cursor
const StreamingCursor: Component = () => (
  <span class="inline-block w-2 h-4 bg-purple-400 animate-pulse ml-0.5" />
);

export const ChatMessage: Component<ChatMessageProps> = (props) => {
  const isUser = () => props.message.role === 'user';

  return (
    <div class={`flex gap-3 ${isUser() ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        class={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium ${
          isUser()
            ? 'bg-gradient-to-br from-blue-500 to-cyan-500'
            : 'bg-gradient-to-br from-purple-500 to-pink-500'
        }`}
      >
        {isUser() ? 'U' : 'AI'}
      </div>

      {/* Content */}
      <div class={`flex-1 max-w-[80%] ${isUser() ? 'text-right' : ''}`}>
        <div
          class={`inline-block rounded-2xl px-4 py-2 ${
            isUser() ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white' : 'bg-gray-700 text-gray-100'
          }`}
        >
          {/* Message text */}
          <p class="whitespace-pre-wrap break-words">
            {props.message.content}
            <Show when={props.message.isStreaming}>
              <StreamingCursor />
            </Show>
          </p>
        </div>

        {/* Tool calls - only for assistant messages */}
        <Show when={!isUser() && props.message.toolCalls?.length}>
          <For each={props.message.toolCalls}>{(toolCall) => <ToolCallCard toolCall={toolCall} />}</For>
        </Show>

        {/* Tool results - only for assistant messages */}
        <Show when={!isUser() && props.message.toolResults?.length}>
          <For each={props.message.toolResults}>{(result) => <ToolResultCard result={result} />}</For>
        </Show>

        {/* Entity cards - only for assistant messages */}
        <Show when={!isUser() && props.message.entities?.length}>
          <For each={props.message.entities}>{(entity) => <EntityCard entity={entity} />}</For>
        </Show>

        {/* Timestamp */}
        <div class={`text-xs text-gray-500 mt-1 ${isUser() ? 'text-right' : ''}`}>
          {new Date(props.message.timestamp).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};
